/**
 * Zod validation schema for the FormCacheSelector save dialog.
 *
 * Exposed as a hook (`useFormCacheSelectorSchema`) so error messages can be
 * resolved through `react-i18next`. Per the Siesa I18N-RULE-004 rule, all
 * validation messages MUST flow through `t(...)` — hardcoded English strings
 * are forbidden.
 *
 * NOTE: the document defining I18N-RULE-004 is NOT in this repository — only this
 * citation and the one in `MasterPatternView/engine/validationEngine.ts`. If the
 * rule turns out to fix a key-naming convention, it prevails over the prefixes
 * this kit chose. Tracked in `docs/i18n-masterdocument-deuda.md` (point 4).
 */

import { useTranslation } from 'react-i18next';
import { z } from 'zod';

/**
 * Returns a Zod schema for the "save form-cache entry" dialog. The schema
 * validates only the user-supplied `name` field (1..50 chars).
 *
 * Must be called from within a React component tree because it uses
 * `useTranslation`.
 */
export function useFormCacheSelectorSchema() {
  const { t } = useTranslation('form-cache-selector');

  return z.object({
    name: z
      .string()
      .min(1, { message: t('validation.name.required') })
      .max(50, { message: t('validation.name.maxLength', { max: 50 }) }),
  });
}
